import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import * as portalSession from '../../auth/portalSession';

/**
 * ProjectDropdown - Quick project switcher in the header
 * Opens on hover (like HelpDropdown and ProfileDropdown)
 */
const ProjectDropdown = ({ currentProjectDetails, compact = false, shrink = false, fullWidth = false, onSelectDepartment, onSelectProject }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { instance, accounts } = useMsal();
  const hasLogin = !!(accounts[0] || portalSession.isActive());
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  // The dropdown panel is rendered through a portal, so it is NOT a DOM descendant of
  // dropdownRef. Without tracking it separately, the click-outside handler treated a
  // tap inside the panel as 'outside' and closed it on touchstart - before the item's
  // click could fire - so on touch devices a selection never registered at all.
  const panelRef = useRef(null);
  const timeoutRef = useRef(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasFetched, setHasFetched] = useState(false);

  // Handle mouse enter - open dropdown (desktop only)
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  // Handle mouse leave - close with delay (desktop only)
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 150);
  };

  // Handle click for mobile/compact mode
  const handleClick = () => {
    if (compact) {
      setIsOpen(!isOpen);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Click outside handler for compact/mobile mode
  useEffect(() => {
    if (!compact || !isOpen) return;
    
    const handleClickOutside = (e) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [compact, isOpen]);

  // Fetch projects when dropdown opens for the first time
  useEffect(() => {
    if (isOpen && !hasFetched && hasLogin) {
      fetchProjects();
    }
  }, [isOpen, hasFetched, accounts, hasLogin]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/projects'
      );
      if (response.data.projects) {
        // Sort by job number
        const sorted = [...response.data.projects].sort((a, b) => {
          const numA = parseInt(a.job_number, 10);
          const numB = parseInt(b.job_number, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return (a.job_number || '').localeCompare(b.job_number || '');
        });
        setProjects(sorted);
        setHasFetched(true);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on search
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects;
    const search = searchTerm.toLowerCase();
    return projects.filter(p =>
      (p.job_number || '').toLowerCase().includes(search) ||
      (p.job_name || '').toLowerCase().includes(search)
    );
  }, [projects, searchTerm]);

  const handleSelectProject = (project) => {
    setIsOpen(false);
    setSearchTerm('');
    // Leaving estimation context — clear sticky estimate from the header
    if (onSelectProject) {
      onSelectProject(project);
    }
    // Auto-select "Project" department when a project is selected
    if (onSelectDepartment) {
      onSelectDepartment('projects');
    }
    navigate(`/project/${project.id}`);
  };

  // Determine display label
  const currentLabel = currentProjectDetails 
    ? `${currentProjectDetails.job_number || ''} - ${currentProjectDetails.job_name || 'Project'}`.trim()
    : projectId 
      ? 'Loading...'
      : 'Select Project';

  // Truncate long labels (shorter for compact mode)
  // Stacked full width there is room for the whole label; the 12-char cap only exists
  // for the tiny side-by-side compact pill.
  const maxLength = fullWidth ? 34 : (compact ? 12 : 30);
  const displayLabel = currentLabel.length > maxLength 
    ? currentLabel.substring(0, maxLength - 3) + '...' 
    : currentLabel;

  // For compact mode, show just the job number or a short label
  const compactLabel = currentProjectDetails?.job_number || (projectId ? '...' : 'Project');

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={!compact ? handleMouseEnter : undefined}
      onMouseLeave={!compact ? handleMouseLeave : undefined}
      style={fullWidth
        ? { position: 'relative', display: 'block', width: '100%', minWidth: 0 }
        : { position: 'relative', display: 'inline-block', flexShrink: shrink ? 1 : 0, overflow: 'hidden', maxWidth: compact ? '110px' : '280px', minWidth: compact ? 48 : 0 }}
    >
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={handleClick}
        data-tutorial="project-dropdown"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '4px' : '6px',
          height: fullWidth ? '40px' : (compact ? '36px' : '44px'),
          width: fullWidth ? '100%' : undefined,
          justifyContent: fullWidth ? 'space-between' : undefined,
          padding: fullWidth ? '0 0.75rem' : (compact ? '0 0.5rem' : '0 1.25rem'),
          background: 'transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          fontSize: fullWidth ? '0.85rem' : (compact ? '0.75rem' : '0.9rem'),
          fontWeight: '500',
          letterSpacing: '0.2px',
          color: 'white',
          boxSizing: 'border-box',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
          e.currentTarget.style.borderColor = '#dc3545';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title={currentLabel}
      >
        {/* Project icon for compact mode */}
        {(compact || fullWidth) && <span style={{ fontSize: '14px', flexShrink: 0 }}>📋</span>}
        
        {/* Project name */}
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}>
          {compact && !fullWidth ? compactLabel : displayLabel}
        </span>
        
        {/* Dropdown arrow */}
        <span style={{
          fontSize: '8px',
          marginLeft: compact ? '2px' : '4px',
          opacity: 0.8,
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          ▼
        </span>
      </button>

      {/* Dropdown Panel - Using Portal */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          onMouseEnter={!compact ? handleMouseEnter : undefined}
          onMouseLeave={!compact ? handleMouseLeave : undefined}
          style={{
            position: 'fixed',
            top: triggerRef.current 
              ? `${triggerRef.current.getBoundingClientRect().bottom + 8}px` 
              : '60px',
            left: compact 
              ? '10px'  // On mobile, anchor to left edge with padding
              : (triggerRef.current 
                ? `${Math.min(triggerRef.current.getBoundingClientRect().left, window.innerWidth - 330)}px` 
                : '100px'),
            right: compact ? '10px' : 'auto',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            zIndex: 9999,
            width: compact ? 'auto' : '320px',
            maxWidth: compact ? 'calc(100vw - 20px)' : '320px',
            maxHeight: compact ? '60vh' : '400px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'dropdownSlideIn 0.15s ease-out',
          }}
        >
          {/* Search Header */}
          <div style={{
            padding: '12px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}>
              Select Project
            </div>
            <input
              type="text"
              placeholder="Search by name or job #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearchTerm('');
                } else if (e.key === 'Enter' && filteredProjects.length === 1) {
                  handleSelectProject(filteredProjects[0]);
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#dc3545'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Projects List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: '320px',
          }}>
            {loading ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #f3f4f6',
                  borderTop: '3px solid #dc3545',
                  borderRadius: '50%',
                  margin: '0 auto 8px',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Loading projects...
              </div>
            ) : filteredProjects.length === 0 ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                {searchTerm ? 'No projects match your search' : 'No projects available'}
              </div>
            ) : (
              filteredProjects.map((project) => {
                const isCurrentProject = String(project.id) === String(projectId);
                return (
                  <div
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: isCurrentProject ? '#fef2f2' : 'transparent',
                      transition: 'background-color 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentProject) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentProject) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* Project Icon/Number */}
                    <div style={{
                      minWidth: '80px',
                      height: '42px',
                      padding: '0 8px',
                      borderRadius: '8px',
                      backgroundColor: isCurrentProject ? '#dc3545' : '#e5e7eb',
                      color: isCurrentProject ? 'white' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '700',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}>
                      {project.job_number || '—'}
                    </div>
                    
                    {/* Project Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {project.job_name || 'Unnamed Project'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: '2px',
                      }}>
                        {project.company_name || '—'}
                      </div>
                    </div>

                    {/* Current indicator */}
                    {isCurrentProject && (
                      <div style={{
                        color: '#dc3545',
                        fontSize: '14px',
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            textAlign: 'center',
          }}>
            <button
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
                navigate('/projects');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc3545',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              View All Projects →
            </button>
          </div>

          {/* Animation Keyframes */}
          <style>{`
            @keyframes dropdownSlideIn {
              from {
                opacity: 0;
                transform: translateY(-8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProjectDropdown;
